import { memo } from 'react';

// ── Types ────────────────────────────────────────────────────────────

interface PermissionGroup {
  id: number | null;
  title: string;
  type?: string;
  permissions: string[];
  requiredRoles: string[];
  allowedRoles: string[];
}

interface PermissionPerson {
  id: number;
  name: string;
  hidden?: boolean;
}

// ── Helper ───────────────────────────────────────────────────────────

function getGroupKey(groupId: number | null): string {
  return groupId === null ? 'new' : String(groupId);
}

// ── Component ────────────────────────────────────────────────────────

interface PermissionsUsersTabProps {
  permissionGroups: PermissionGroup[];
  sortedPeople: PermissionPerson[];
  selectedPeople: number[];
  isPersonSelected: (personId: number) => boolean;
  isPersonInGroup: (personId: number, groupId: number | null) => boolean;
  onTogglePersonSelection: (personId: number) => void;
  onAddSelectedToGroup: (groupId: number | null) => void;
  onRemoveSelectedFromGroup: (groupId: number | null) => void;
}

export const PermissionsUsersTab = memo(function PermissionsUsersTab({
  permissionGroups,
  sortedPeople,
  selectedPeople,
  isPersonSelected,
  isPersonInGroup,
  onTogglePersonSelection,
  onAddSelectedToGroup,
  onRemoveSelectedFromGroup,
}: PermissionsUsersTabProps) {
  return (
    <div className="permissions-content">
      <div className="users-matrix">
        <table className="users-table">
          <thead>
            <tr>
              <th className="user-name-header">Users</th>
              {permissionGroups.map((group) => (
                <th key={'uhdr-' + getGroupKey(group.id)} className="group-header">
                  <span className="group-title">{group.title}</span>
                  <div className="group-actions">
                    <button
                      className="btn btn-success btn-xs"
                      data-add-to-group={String(group.id)}
                      disabled={selectedPeople.length === 0}
                      title="Add selected users to this group"
                      onClick={() => onAddSelectedToGroup(group.id)}
                    >
                      <i className="fa fa-user-plus"></i>
                    </button>
                    <button
                      className="btn btn-danger btn-xs"
                      data-remove-from-group={String(group.id)}
                      disabled={selectedPeople.length === 0}
                      title="Remove selected users from this group"
                      onClick={() => onRemoveSelectedFromGroup(group.id)}
                    >
                      <i className="fa fa-user-minus"></i>
                    </button>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedPeople.map((person) => (
              <tr key={'user-' + person.id} className="user-row" data-person-id={String(person.id)}>
                <td
                  className={'user-name' + (isPersonSelected(person.id) ? ' selected' : '')}
                  data-person-id={String(person.id)}
                  onClick={() => onTogglePersonSelection(person.id)}
                >
                  {person.name}
                </td>
                {permissionGroups.map((group) => {
                  const inGroup = isPersonInGroup(person.id, group.id);
                  return (
                    <td
                      key={'ucell-' + person.id + '-' + getGroupKey(group.id)}
                      className={
                        'user-group-cell' +
                        (inGroup ? ' in-group' : '') +
                        (isPersonSelected(person.id) ? ' selected' : '')
                      }
                    >
                      {inGroup ? person.name : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});
